import { Dispatch } from '@reduxjs/toolkit';
import axiosInstance, { ApiKeys, getApiUrl, logError } from '../../../utils/ApiHelper';
import {
  setCaseDetail,
  setCaseDetailLoading,
  setContacts,
  setCustomerConfidence,
  setCustomerInfo,
  setEmiBreakup,
  setEmiBreakupError,
  setEmiBreakupLoading,
  setTelephones,
  setTelephonesV2
} from '../reducers/caseDetailSlice';
import { CONTACT_TYPES, STORE_KEYS, ToastMessage } from '../constants';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { OverdueResponse } from '../interfaces/EmiSchedule.type';

export const getCustomerDetails =
  (
    loanaccountnumber: string,
    customerreferenceid: string,
    caseReferenceId: string,
    caseBusinessVertical: string
  ) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan: loanaccountnumber,
        customerRefrenceId: customerreferenceid,
        key: STORE_KEYS.DETAILS,
        loading: true
      })
    );
    axiosInstance
      .get(getApiUrl(ApiKeys.DETAILS), {
        headers: {
          customerreferenceid,
          loanaccountnumber,
          caseReferenceId,
          caseBusinessVertical,
          includeInUptimeMetrics: true
        }
      })
      .then(res => {
        dispatch(
          setCaseDetail({
            lan: loanaccountnumber,
            customerRefrenceId: customerreferenceid,
            key: STORE_KEYS.DETAILS,
            data: res.data
          })
        );
      });
  };

export const getTelephones =
  (
    loanaccountnumber: string,
    customerreferenceid: string,
    caseReferenceId: string,
    caseBusinessVertical: string
  ) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan: loanaccountnumber,
        customerRefrenceId: customerreferenceid,
        key: STORE_KEYS.TELEPHONES_V2,
        loading: true
      })
    );
    axiosInstance
      .get(getApiUrl(ApiKeys.TELEPHONES), {
        headers: { customerreferenceid, loanaccountnumber, caseReferenceId, caseBusinessVertical }
      })
      .then(res => {
        dispatch(
          setTelephones({
            lan: loanaccountnumber,
            customerRefrenceId: customerreferenceid,
            data: res.data
          })
        );
      });
  };

export const getContacts =
  (loanAccountNumber: string, customerReferenceId: string, contactType: CONTACT_TYPES) =>
  (dispatch: Dispatch) => {
    axiosInstance
      .get(getApiUrl(ApiKeys.FETCH_CONTACTS, { contactType }), {
        headers: { loanAccountNumber, customerReferenceId }
      })
      .then(res => {
        dispatch(
          setContacts({
            lan: loanAccountNumber,
            customerRefrenceId: customerReferenceId,
            data: res.data.data,
            contactType: contactType
          })
        );
      });
  };

export const getCustomerInfo = (lan: string, customerRefId: string) => (dispatch: Dispatch) => {
  axiosInstance
    .get(
      getApiUrl(
        ApiKeys.FETCH_CUSTOMER_INFO,
        { customerRefId },
        { withVerifiedBankAccount: 'true' }
      ),
      { headers: { includeInUptimeMetrics: true } }
    )
    .then(res => {
      dispatch(
        setCustomerInfo({
          lan: lan,
          customerRefrenceId: customerRefId,
          data: res.data
        })
      );
    })
    .catch(() => {
      dispatch(
        setCustomerInfo({
          lan,
          customerRefrenceId: customerRefId,
          key: {}
        })
      );
    });
};

export const shareEnachLink =
  (
    loanaccountnumber: string,
    customerreferenceid: string,
    successCallback: () => void,
    caseReferenceId: string,
    caseBusinessVertical: string
  ) =>
  () => {
    axiosInstance
      .post(
        getApiUrl(ApiKeys.REGISTER_ENACH_LINK),
        {},
        {
          headers: { customerreferenceid, loanaccountnumber, caseReferenceId, caseBusinessVertical }
        }
      )
      .then(() => {
        successCallback();
        toast(ToastMessage.ENACH_LINK_SHARED_SUCESSFULLY, { type: 'success' });
      });
  };

export const getEMIBreakUp =
  (lan: string, customerRefrenceId: string, refId: string) => (dispatch: Dispatch) => {
    dispatch(
      setEmiBreakupLoading({
        lan,
        customerRefrenceId,
        key: refId,
        loading: true
      })
    );
    axiosInstance
      .get(getApiUrl(ApiKeys.GET_EMI_BREAKUP, { refId }))
      .then(res => {
        dispatch(
          setEmiBreakup({
            lan,
            customerRefrenceId,
            key: refId,
            data: res.data
          })
        );
      })
      .catch(() => {
        dispatch(
          setEmiBreakupError({
            lan,
            customerRefrenceId,
            key: refId
          })
        );
      });
  };

export const calculateOverDue =
  (
    lan: string,
    date: string,
    successCallback: (data: OverdueResponse) => void,
    failureCallback: () => void
  ) =>
  () => {
    axiosInstance
      .get(getApiUrl(ApiKeys.CALCULATE_OVERDUE, { lan, date }))
      .then(res => {
        successCallback(res.data);
      })
      .catch(failureCallback);
  };

export const getTelephonesV2 =
  (
    loanaccountnumber: string,
    customerreferenceid: string,
    caseReferenceId: string,
    caseBusinessVertical: string,
    successCallBack?: () => void
  ) =>
  (dispatch: Dispatch) => {
    dispatch(
      setCaseDetailLoading({
        lan: loanaccountnumber,
        customerRefrenceId: customerreferenceid,
        key: STORE_KEYS.TELEPHONES,
        loading: true
      })
    );
    axiosInstance
      .get(getApiUrl(ApiKeys.TELEPHONES_V2), {
        headers: {
          customerreferenceid,
          loanaccountnumber,
          caseReferenceId,
          caseBusinessVertical,
          includeInUptimeMetrics: true
        }
      })
      .then(res => {
        dispatch(
          setTelephonesV2({
            lan: loanaccountnumber,
            customerRefrenceId: customerreferenceid,
            data: res.data
          })
        );
        successCallBack && successCallBack();
      });
  };

export const removeContact = (
  customerReferenceId: string,
  contact: string,
  cb: (isModalOpen: boolean) => void,
  successCallback: () => void
) => {
  const url = getApiUrl(ApiKeys.REMOVE_CONTACT);
  return axiosInstance
    .post(
      url,
      {
        telephoneNumbers: [contact]
      },
      {
        headers: { customerReferenceId: customerReferenceId }
      }
    )
    .then(res => {
      toast('Contact removed successfully', { type: 'success' });
      successCallback();
      cb(false);
    })
    .catch(err => {
      toast('Unable to remove contact', { type: 'error' });
    });
};

export const getCustomerConfidence = () => (dispatch: Dispatch) => {
  axiosInstance
    .get(getApiUrl(ApiKeys.CUSTOMER_CONFIDENCE))
    .then(res => {
      dispatch(setCustomerConfidence(res.data));
    })
    .catch(() => {
      logError('Error fetching customer confidence');
    });
};
